import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Activity, Wallet, BarChart3, MapPin, Shield, Cpu, Watch, Brain, HeartHandshake, Sparkles, CheckCircle2 } from "lucide-react";

// Import translations
import watchAssistantTranslations from "./locales/watchAssistant.json";

interface FeatureItem {
    id: string;
    title: string;
    description: string;
    icon: React.ReactNode;
    color: string;
}

interface WidgetItem {
    id: string;
    title: string;
    description: string;
    icon: React.ReactNode;
    color: string;
    bgColor: string;
}

const WatchAssistantSection: React.FC = () => {
    const { t, i18n } = useTranslation("watchAssistant");
    const isRTL = ["fa", "ar"].includes(i18n.language);

    const [activeFeature, setActiveFeature] = useState<string>("activity-status");
    const [isVisible, setIsVisible] = useState(false);
    const [currentWidgetIndex, setCurrentWidgetIndex] = useState(0);
    const [showWidgets, setShowWidgets] = useState(false);

    // Load translations
    useEffect(() => {
        const ns = "watchAssistant";
        ["fa", "en", "ar", "tr"].forEach((lng) => {
            const translations = watchAssistantTranslations[lng as keyof typeof watchAssistantTranslations];
            if (translations && !i18n.hasResourceBundle(lng, ns)) {
                i18n.addResourceBundle(lng, ns, translations, true, true);
            }
        });
    }, [i18n]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsVisible(entry.isIntersecting);
            },
            { threshold: 0.2 }
        );

        const element = document.getElementById("watch-assistant-section");
        if (element) observer.observe(element);

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!showWidgets) return;

        const interval = setInterval(() => {
            setCurrentWidgetIndex((prev) => (prev + 1) % 4);
        }, 3000);

        return () => clearInterval(interval);
    }, [showWidgets]);

    const handleImageInteraction = useCallback(() => {
        setShowWidgets((prev) => !prev);
    }, []);

    // Icon mapping
    const getFeatureIcon = (id: string): React.ReactNode => {
        const iconMap: Record<string, React.ReactNode> = {
            "activity-status": <Activity className="w-5 h-5 text-white" />,
            "financial-wallet": <Wallet className="w-5 h-5 text-white" />,
            "smart-weekly": <BarChart3 className="w-5 h-5 text-white" />,
            "movement-history": <MapPin className="w-5 h-5 text-white" />,
            "monitoring-boundaries": <Shield className="w-5 h-5 text-white" />,
            "digital-activity": <Cpu className="w-5 h-5 text-white" />,
        };
        return iconMap[id] || <Activity className="w-5 h-5 text-white" />;
    };

    const getWidgetIcon = (id: string): React.ReactNode => {
        const iconMap: Record<string, React.ReactNode> = {
            processing: <Watch className="w-5 h-5 text-white" />,
            secure: <Shield className="w-5 h-5 text-white" />,
            "ai-analysis": <Brain className="w-5 h-5 text-white" />,
            wellness: <HeartHandshake className="w-5 h-5 text-white" />,
        };
        return iconMap[id] || <Watch className="w-5 h-5 text-white" />;
    };

    // Orange/Amber Theme Color mapping
    const getFeatureColor = (id: string): string => {
        const colorMap: Record<string, string> = {
            "activity-status": "from-orange-500 to-amber-500",
            "financial-wallet": "from-amber-500 to-yellow-500",
            "smart-weekly": "from-orange-600 to-red-500",
            "movement-history": "from-amber-600 to-orange-500",
            "monitoring-boundaries": "from-orange-500 to-amber-600",
            "digital-activity": "from-amber-500 to-orange-600",
        };
        return colorMap[id] || "from-orange-500 to-amber-500";
    };

    const getWidgetColor = (id: string): string => {
        const colorMap: Record<string, string> = {
            processing: "from-orange-500 to-amber-500",
            secure: "from-amber-500 to-orange-600",
            "ai-analysis": "from-orange-600 to-amber-600",
            wellness: "from-amber-600 to-yellow-500",
        };
        return colorMap[id] || "from-orange-500 to-amber-500";
    };

    const getWidgetBgColor = (id: string): string => {
        return "bg-amber-50/90";
    };

    const getFeaturesData = (): FeatureItem[] => {
        const featuresData = t("features", { returnObjects: true });
        if (!Array.isArray(featuresData)) return [];

        return featuresData.map((feature: any) => ({
            ...feature,
            icon: getFeatureIcon(feature.id),
            color: getFeatureColor(feature.id),
        }));
    };

    const getWidgetsData = (): WidgetItem[] => {
        const widgetsData = t("widgets", { returnObjects: true });
        if (!Array.isArray(widgetsData)) return [];

        return widgetsData.map((widget: any) => ({
            ...widget,
            icon: getWidgetIcon(widget.id),
            color: getWidgetColor(widget.id),
            bgColor: getWidgetBgColor(widget.id),
        }));
    };

    const features = getFeaturesData();
    const widgetData = getWidgetsData();

    if (features.length === 0 || widgetData.length === 0) {
        return null;
    }

    return (
        <section id="watch-assistant-section" className="relative py-12 md:py-20 rounded-3xl px-4 md:px-10 mb-12 md:mb-20 overflow-hidden bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-amber-600/10 border border-amber-200/60 shadow-xl shadow-orange-500/5" dir={isRTL ? "rtl" : "ltr"}>
            {/* Ambient Background Glows */}
            <div className="absolute top-0 right-1/4 w-96 h-96 bg-gradient-to-br from-orange-400/20 to-amber-300/10 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-gradient-to-tl from-amber-500/15 to-orange-400/10 rounded-full blur-3xl pointer-events-none" />

            <div className="relative max-w-7xl mx-auto z-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
                    
                    {/* Left Column: Header and Feature Cards */}
                    <div className="lg:col-span-7 space-y-8">
                        {/* Header */}
                        <div className={`text-center ${isRTL ? "lg:text-right" : "lg:text-left"}`}>
                            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-100/90 border border-orange-200 text-orange-900 text-xs sm:text-sm font-semibold mb-4 shadow-xs">
                                <Watch className="w-4 h-4 text-orange-600 shrink-0" />
                                <span>{t("badge.text")}</span>
                            </div>

                            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 mb-4 leading-tight tracking-tight">
                                {t("header.title")}
                            </h2>

                            <p className="text-slate-700 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto lg:mx-0">
                                {t("header.description")}
                            </p>
                        </div>

                        {/* Features Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {features.map((feature) => {
                                const isActive = activeFeature === feature.id;
                                return (
                                    <div
                                        key={feature.id}
                                        onClick={() => setActiveFeature(feature.id)}
                                        className={`
                                            group relative p-4 rounded-2xl cursor-pointer transition-all duration-300 border
                                            ${
                                                isActive
                                                    ? "bg-white/90 backdrop-blur-md border-orange-400 shadow-lg shadow-orange-500/10 -translate-y-0.5"
                                                    : "bg-white/60 backdrop-blur-sm border-slate-200/80 hover:bg-white/80 hover:border-orange-300"
                                            }
                                        `}
                                    >
                                        <div className="flex items-start gap-3.5">
                                            {/* Icon */}
                                            <div
                                                className={`
                                                    p-3 rounded-xl bg-gradient-to-br ${feature.color} 
                                                    shadow-md group-hover:scale-105 transition-transform duration-300 shrink-0
                                                `}
                                            >
                                                {feature.icon}
                                            </div>

                                            {/* Text Content */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between gap-1">
                                                    <h3 className="text-slate-900 font-bold text-sm sm:text-base group-hover:text-orange-600 transition-colors truncate">
                                                        {feature.title}
                                                    </h3>
                                                    {isActive && (
                                                        <CheckCircle2 className="w-4 h-4 text-orange-500 shrink-0" />
                                                    )}
                                                </div>
                                                <p className="text-slate-600 text-xs mt-1 leading-relaxed line-clamp-2">
                                                    {feature.description}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Right Column: Watch Illustration & Interactive Modal */}
                    <div className="lg:col-span-5 flex flex-col items-center justify-center relative">
                        <div
                            className="relative w-full max-w-md group cursor-pointer"
                            onClick={handleImageInteraction}
                            onMouseEnter={() => setShowWidgets(true)}
                            onMouseLeave={() => setShowWidgets(false)}
                        >
                            {/* Watch Image */}
                            <div className="relative rounded-3xl p-4 overflow-hidden">
                                <motion.img
                                    src="/media/a2.webp"
                                    alt="Watch Assistant"
                                    className="w-full h-auto object-contain mx-auto drop-shadow-2xl transition-transform duration-500 group-hover:scale-105"
                                    whileHover={{ scale: 1.03 }}
                                />
                                
                                {/* Overlay instruction hint */}
                                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-slate-900/75 backdrop-blur-md text-white text-xs font-medium border border-white/20 shadow-lg flex items-center gap-2 opacity-90 group-hover:opacity-100 transition-opacity">
                                    <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                                    <span>{showWidgets ? (isRTL ? "پایش فعال است" : "Monitoring Active") : (isRTL ? "برای نمایش جزئیات کلیک کنید" : "Click to view details")}</span>
                                </div>
                            </div>

                            {/* Redesigned Modal / Overlay Card */}
                            <AnimatePresence>
                                {showWidgets && (
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.95, y: 10 }}
                                        animate={{ opacity: 1, scale: 1, y: 0 }}
                                        exit={{ opacity: 0, scale: 0.95, y: 10 }}
                                        transition={{ duration: 0.3 }}
                                        className="absolute inset-0 z-20 bg-white/95 backdrop-blur-xl rounded-3xl p-5 border border-orange-200 shadow-2xl flex flex-col justify-between overflow-hidden"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {/* Modal Top Header */}
                                        <div className="flex items-center justify-between pb-3 border-b border-orange-100 mb-2">
                                            <div className="flex items-center gap-2">
                                                <span className="w-2.5 h-2.5 rounded-full bg-orange-500 animate-pulse" />
                                                <h4 className="font-bold text-slate-900 text-sm sm:text-base">
                                                    {isRTL ? "پایش هوشمند ساعت" : "Watch Smart Monitor"}
                                                </h4>
                                            </div>
                                            <button
                                                onClick={() => setShowWidgets(false)}
                                                className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-orange-50 text-orange-700 hover:bg-orange-100 transition-colors"
                                            >
                                                {isRTL ? "بستن" : "Close"}
                                            </button>
                                        </div>

                                        {/* Modal Widget Items */}
                                        <div className="space-y-2.5 my-auto">
                                            {widgetData.map((widget, index) => {
                                                const isCurrent = currentWidgetIndex === index;
                                                return (
                                                    <div
                                                        key={widget.id}
                                                        onClick={() => setCurrentWidgetIndex(index)}
                                                        className={`
                                                            p-3 rounded-2xl border transition-all duration-300 cursor-pointer flex items-center gap-3
                                                            ${
                                                                isCurrent
                                                                    ? "bg-gradient-to-r from-orange-50 to-amber-50 border-orange-300 shadow-md shadow-orange-500/10"
                                                                    : "bg-slate-50/80 border-slate-100 hover:bg-orange-50/50"
                                                            }
                                                        `}
                                                    >
                                                        <div className={`p-2.5 rounded-xl bg-gradient-to-br ${widget.color} shadow-sm text-white shrink-0`}>
                                                            {widget.icon}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center justify-between">
                                                                <h5 className="font-bold text-slate-900 text-xs sm:text-sm truncate">
                                                                    {widget.title}
                                                                </h5>
                                                            </div>
                                                            <p className="text-slate-600 text-[11px] leading-tight line-clamp-1 mt-0.5">
                                                                {widget.description}
                                                            </p>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {/* Modal Bottom Progress Indicator */}
                                        <div className="pt-3 border-t border-orange-100 flex items-center justify-between text-xs text-slate-500">
                                            <span>{isRTL ? "بروزرسانی زنده" : "Live Update"}</span>
                                            <div className="flex items-center gap-1.5">
                                                {widgetData.map((_, idx) => (
                                                    <button
                                                        key={idx}
                                                        onClick={() => setCurrentWidgetIndex(idx)}
                                                        className={`
                                                            h-1.5 rounded-full transition-all duration-300
                                                            ${currentWidgetIndex === idx ? "w-6 bg-orange-500" : "w-1.5 bg-slate-300"}
                                                        `}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>

                </div>
            </div>
        </section>
    );
};

export default WatchAssistantSection;
